/**
 * Canonical phone-number normalization helpers.
 *
 * These consolidate the loose Brazilian-9th-digit matching logic that was
 * duplicated across at least four packages
 * (`backend/authentication/src/sso/utils.ts`, `backend/feedback/src/helpers.ts`,
 * `libraries/profile-graduation`, and `libraries/gym-payment`).
 *
 * A phone number is a mutable *attribute* of a user, never an identity key.
 * These helpers exist only to resolve a phone to the canonical
 * `profile_user.identificator.uuid` exactly once at a system boundary
 * (see `resolve.ts`). Once data carries a uuid, comparisons are exact.
 */

/**
 * Strip every non-digit character. Accepts `unknown` because phone values
 * frequently arrive untyped (query params, Mongo payloads, WhatsApp ids).
 */
export function digitsOnly(phone: unknown): string {
  return String(phone ?? '').replace(/\D/g, '')
}

/**
 * Normalize a phone (or WhatsApp id like `5511978775084@c.us`) into DDI-55
 * digits: strips the `@…` suffix and formatting, then prefixes `55` when the
 * number looks like a bare Brazilian local number (10–11 digits). Does NOT
 * touch the 9th digit — use [canonicalPhoneKey] for a 9th-digit-insensitive
 * key. This is the payment/WhatsApp identity normalization previously owned
 * by `@goodfellas/gym-payment`.
 */
export function normalizePhone(raw: string | null | undefined): string {
  const digits = String(raw ?? '')
    .split('@')[0]
    .replace(/\D/g, '')
  if (!digits) return ''
  if (digits.startsWith('55')) return digits
  if (digits.length === 10 || digits.length === 11) return `55${digits}`
  return digits
}

/**
 * Deterministic canonical key for a phone number, tolerant of the optional
 * Brazilian mobile 9th digit. Both `5511978775084` and `551178775084`
 * collapse to the same key, so it can be used as an exact lookup/index key
 * (unlike the regexes below, which are for matching legacy stored values).
 */
export function canonicalPhoneKey(phone: unknown): string {
  const digits = digitsOnly(phone)
  if (/^55\d{10,11}$/.test(digits)) {
    const countryDdd = digits.slice(0, 4) // 55 + DDD
    const rest = digits.slice(4)
    const base =
      rest.startsWith('9') && rest.length === 9 ? rest.slice(1) : rest
    return `${countryDdd}${base}`
  }
  return digits
}

/**
 * Build a regex that matches a stored phone number regardless of formatting,
 * tolerating the optional Brazilian 9th digit. Used to match legacy values
 * stored without normalization (e.g. `profile_user.phone.payload.number`).
 */
export function getSearchNumber(phone: string): RegExp {
  const digits = digitsOnly(phone)
  if (/^55\d{10,11}$/.test(digits)) {
    const ddd = digits.slice(0, 4)
    const restWithNine = digits.slice(4)
    const restWithoutNine =
      restWithNine.startsWith('9') && restWithNine.length === 9
        ? restWithNine.slice(1)
        : restWithNine

    return new RegExp(`^${ddd}9?${restWithoutNine}$`)
  }

  return new RegExp(`^${digits}$`)
}

/** True when two phone numbers are equal up to the optional Brazilian 9th digit. */
export function isEqualWithNinthDigit(
  phoneNumberInit: string,
  phoneNumberEnd: string,
): boolean {
  const initDigits = digitsOnly(phoneNumberInit)
  const endDigits = digitsOnly(phoneNumberEnd)
  if (!/^55/.test(initDigits)) return initDigits === endDigits

  const ddd = initDigits.slice(0, 4)
  const restWithNine = initDigits.slice(4)
  const restWithoutNine =
    restWithNine.startsWith('9') && restWithNine.length === 9
      ? restWithNine.slice(1)
      : restWithNine

  return (
    endDigits === `${ddd}9${restWithoutNine}` ||
    endDigits === `${ddd}${restWithoutNine}`
  )
}

/**
 * Regex that matches a phone number regardless of formatting (allows non-digit
 * separators between every digit) and the optional Brazilian 9th digit. Matches
 * both `5511978775084` and `+55 11 97877-5084`.
 */
export function digitsToLooseRegex(phone: string): RegExp {
  const digits = digitsOnly(phone)
  if (!digits) return new RegExp('^$')

  if (/^55\d{10,11}$/.test(digits)) {
    const ddd = digits.slice(0, 4)
    const restWithNine = digits.slice(4)
    const restWithoutNine =
      restWithNine.startsWith('9') && restWithNine.length === 9
        ? restWithNine.slice(1)
        : restWithNine

    const dddPattern = ddd.split('').join('\\D*')
    const restPattern = restWithoutNine.split('').join('\\D*')

    return new RegExp(`^\\+?\\D*${dddPattern}\\D*9?\\D*${restPattern}\\D*$`)
  }

  const pattern = digits.split('').join('\\D*')
  return new RegExp(`^\\+?\\D*${pattern}\\D*$`)
}
